<template>
  <div class="cutsheet-viewer__container">

    <aside 
      class="menu cutsheet-viewer__nav noprint">
      <p class="menu-label">
        Cut Sheets for Project
      </p>
      <div class="mb-5">
        {{ cutSheet.project.project_name }}
      </div>

      <v-fade-transition mode="out-in">
        <div 
          v-if="cutSheetsLoading" 
          class="is-relative">
          <v-spin-loader :value="true">
            <div class="py-6"></div>
          </v-spin-loader>
        </div>
        <v-alert 
          class="my-3"
          v-if="!cutSheetsLoading && cutSheets.length == 0"
          type="info">
          There are no other cut sheets for this project.
        </v-alert>
        <ul v-if="cutSheets.length > 0" class="menu-list">
          <li 
            v-for="cutSheetItem in cutSheets" 
            :key="cutSheetItem.id">
            <a 
              @click="goToCutSheet(cutSheetItem)"
              :class="{ 'is-active': cutSheetItem.id == cutSheet.id }">
              {{ cutSheetItem.cutsheet_no }}
            </a>
          </li>
        </ul>
      </v-fade-transition>
    </aside>

    <div class="cutsheet-viewer__cutsheet">
      <div class="columns is-multiline pb-2 header" v-if="cutSheet">
        <div class="column is-2">
          <label class="label">Project Name</label>
          <span class="is-size-6">
            {{ cutSheet.project.project_name }}
          </span>
        </div>
        <div class="column is-2">
          <label class="label">Cut Sheet No.</label>
          <span class="is-size-6">
            {{ cutSheet.cutsheet_no }}
          </span>
        </div>
        <div class="column is-1">
          <label class="label">Tag</label>
          <span class="is-size-6">
            {{ cutSheet.item }}
          </span>
        </div>
        <div class="column is-3">
          <label class="label">Product</label>
          <span v-if="cutSheet.input_product_master" class="is-size-6">
            {{ cutSheet.input_product_master.name }}
          </span>
          <span class="help">
            Thickness: {{ cutSheet.thick_in_str }} inches
          </span>
        </div>
        <div class="column is-2">
          <label class="label">Created Date</label>
          <span class="is-size-6">
            {{ formatDate(parse(cutSheet.created_on)) }}
          </span>
        </div>
        <div class="column is-2">
          <label class="label">Expected Delivery Date</label>
          <span v-if="cutSheet.expected_delivery_date" class="is-size-6">
            {{ formatDate(parse(cutSheet.expected_delivery_date)) }}
          </span>
          <span v-else class="has-text-grey-light">
            Not Available
          </span>
        </div>
      </div>

      <table class="table is-fullwidth is-hoverable has-smallish-font sticky-header">
        <thead>
          <tr class="has-text-centered is-capitalized">
            <th 
              v-for="(header, headerIndex) in headers" 
              :key="headerIndex"
              v-bind="header.bind">
              {{ header.label }}
            </th>
            <th 
              class="steps"
              v-for="step in cutSheetSteps" 
              :key="'step-' + step.id">
              {{ step.name }}
            </th>
            <!-- <th class="noprint">Assembly Details</th>
            <th class="noprint">Skid No.</th> -->
          </tr>
        </thead>

        <tbody v-if="cutSheet">
          <tr
            class="has-text-centered v-centered" 
            v-for="part in cutSheet.parts" :key="part.id">

            <td 
              v-for="(header, headerIndex) in headers" 
              :key="headerIndex"
              v-bind="header.bind">
              <span v-if="header.field != 'attachment'">
                <template v-if="header.filter">
                  {{ dynamicFilter(
                    header.filter, header.filterArgs, part[header.field]
                    ) }}
                </template>
                <template v-else>
                  {{ part[header.field] }}
                </template>
              </span>
              <a 
                v-if="header.field == 'attachment' && part.attachment"
                @click="launchPreview(part)">
                attachment
              </a>
            </td>

            <td 
              v-for="step in cutSheetSteps" 
              :key="'step-' + step.id"
              :class="{ 
                'print-empty': !part.steps[step.id]
              }">
              <v-styled-checkbox 
                class="noprint"
                v-if="part.steps[step.id]"
                v-model="part.steps[step.id].is_completed"
                @input="changeCompletionStatus(part.id, part.steps[step.id].id, $event)">
              </v-styled-checkbox>
              <div
                v-if="part.steps[step.id] && part.steps[step.id].is_completed" 
                class="printonly has-text-centered">
                &#10003;
              </div>
            </td>
            <!-- For assembly, show link to assembly -->
            <!-- <td class="noprint">
              <router-link 
                :to="{ 
                  name: 'AssemblyView', 
                  params: { 
                    assemblySheetId: part.assembly_detail.assemblysheet_id 
                  }
                }">
                <a v-if="part.assembly_detail.assemblysheet_id != 0">
                  Assembly Details
                </a>
              </router-link>
            </td> -->

            <!-- For skid, show link to skid -->
            <!-- <td class="noprint">
              <router-link 
                :to="{ name: 'SkidView', params: { skidId: part.skid.id }}">
                <a v-if="part.skid.id != 0">
                  {{ part.skid.skid_no }}
                </a>
              </router-link>
            </td> -->
          </tr>
        </tbody>
      </table>
    </div>

    <v-file-preview
      v-model="preview.state"
      :src="previewSrc"
      :tag="attachmentType">
    </v-file-preview>

  </div>
</template>

<script>
import VFilePreview from '@/components/base/VFilePreview'
import VStyledCheckbox from '@/components/base/VStyledCheckbox'
import VSpinLoader from '@/components/base/VSpinLoader'
import VAlert from '@/components/base/VAlert'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VStyledCheckbox, VFilePreview, VSpinLoader, VAlert, VFadeTransition 
  },
  data() {
    return {
      cutSheet: null,
      cutSheets: [],
      cutSheetsLoading: false,
      headers: [
        { label: 'Part #', field: 'part_no', bind: { class: 'border--after' } },
        { label: 'W"', field: 'width_in_str' },
        { label: 'L"', field: 'length_in_str', bind: { class: 'border--after' } },
        { label: 'Wmm', field: 'width_mm', filter: 'round', filterArgs: 0 },
        { label: 'Lmm', field: 'length_mm', filter: 'round', filterArgs: 0 },
        { label: 'Quantity', field: 'quantity', bind: { class: 'border--before border--after' } },
        { label: 'Total Sqft', field: 'total_sqft', filter: 'round', filterArgs: 0 },
        { label: 'Shop Ticket', field: 'attachment', bind: { class: 'shop-ticket' } },
        { label: 'Assembly', field: 'requires_assembly', bind: { class: 'border--before' } }
      ],
      preview: {
        state: false,
        partId: null
      },
      parse: (d) => {
        return d ? parseISO(d) : null;
      },
      formatDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      }
    }
  },
  computed: {
    ...mapState("cutSheets", ["cutSheetSteps"]),
    cutSheetId() {
      return this.$route.params.cutSheetId;
    },
    previewSrc() {
      if (this.preview.partId) {
        return this.cutSheet.parts.filter(d => d.id == this.preview.partId)[0].attachment;
      } else {
        return null
      }
    },
    attachmentType({ previewSrc }) {
      if (previewSrc) {
        if (previewSrc.includes('.pdf')) {
          return 'iframe'
        } else {
          return 'img'
        }
      } else {
        return null
      }
    }
  },
  created() {
    this.initialize();

    if (this.cutSheetSteps.length == 0) {
      this.getSteps();
    }
  },
  watch: {
    cutSheetId() {
      this.initialize();
    }
  },
  methods: {
    ...mapActions("cutSheets", ["getSteps"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route.params.cutSheet) {
        this.cutSheet = this.$route.params.cutSheet;
        if (this.cutSheets.length == 0) {
          this.getCutSheets(this.cutSheet.project.id);
        } else if (this.cutSheets[0].project.id != this.cutSheet.project.id) {
          this.getCutSheets(this.cutSheet.project.id);
        }
      } else {
        this.getCutSheet();
      }
    },
    changeCompletionStatus(partId, stepId, status) {
      this.$http.cutsheetPartSteps.put(stepId, { is_completed: status }, { cutsheetpart_id: partId });
    },
    getCutSheet() {
      this.$http.cutsheet.get(this.$route.params.cutSheetId)
        .then(resp => {
          this.cutSheet = resp.data.results[0];
          this.getCutSheets(resp.data.results[0].project.id);
        });
    },
    getCutSheets(projectId) {
      this.cutSheetsLoading = true;
      this.$http.cutsheets.get({ project_id: projectId })
        .then(resp => {
          this.cutSheets = resp.data.results;
          this.cutSheetsLoading = false;
        }).catch(() => this.cutSheetsLoading = false);
    },
    goToCutSheet(cutSheet) {
      this.$router.push({
        name: 'CutSheetViewer',
        params: {
          cutSheetId: cutSheet.id,
          cutSheet: cutSheet
        }
      })
    },
    launchPreview(part) {
      this.preview.partId = part.id;
      this.preview.state = true;
    },
    dynamicFilter(name, args, v) {
      return this.$options.filters[name](v, args);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
@import '@/styles/printable.scss';

.cutsheet-viewer {
  &__container {
    display: flex;
    flex-direction: row;
  }
  &__nav {
    width: 15%;
    padding: 1em;
    border-right: 1px solid gainsboro;
    overflow: auto;
    height: calc(100vh - 52px);
  }
  &__cutsheet {
    width: 85%;
    padding-top: 1em;
    padding-bottom: 1em;
    padding-right: 1.5em;
    padding-left: 1em;
  }
}

@media print {
  .cutsheet-viewer__cutsheet {
    padding-top: 0 !important;
    width: 27.94cm;
  }
  header {
    position: fixed;
    top: 0;
    page-break-before: always;
  }
  .table td.shop-ticket, .table th.shop-ticket {
    display: none;
  }
  .table th {
    font-size: 0.7em;
  }
  .table td {
    font-size: 0.75em;
  }
  .print-empty {
    // box-shadow: inset 0 0 0 1000px gainsboro;
    background: repeating-linear-gradient( 45deg, #ccc, #ccc 10px, #aaa 10px, #aaa 20px );
  }
}

</style>